import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import ArrowLeftRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowLeftRight';
import Link2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Link2';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import LWForm from 'components/LWForm';
import FadeCollapse from 'components/standard/FadeCollapse';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import HookedTextField from 'components/standard/Form/HookedTextField';
import PaymentMethodIcon from 'components/standard/PaymentMethodIcon';
import { creditCardValidators } from 'modules/creditCard';
import useBillingPaymentProfileDetails from 'modules/queries/billing/payment/profile/useDetails';
import { useDialogMakePaymentContext } from '../dialogMakePaymentContext';
import AmountField from './AmountField';
import ChoosePaymentMethodContent from './ChoosePaymentMethod';

const PaymentMethodTile = ({ sx, ...rest }: BoxProps) => (
	<Box
		sx={combineSx(
			({ borderRadius, vars }) => ({
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 100,
				borderRadius: borderRadius.rounded,
				border: '1px solid',
				borderColor: vars.palette.uc.border.weak,
				padding: 200,
				width: '100%',
			}),
			sx,
		)}
		{...rest}
	/>
);

const PaymentMethod = () => {
	const { data: paymentProfileDetails, isLoading } =
		useBillingPaymentProfileDetails({});
	const {
		payment_method: paymentMethod,
		issuer,
		cc_exp: expirationDate,
		masked_number: maskedNumber,
	} = paymentProfileDetails || {};

	if (isLoading) {
		return (
			<PaymentMethodTile>
				<Skeleton variant="rounded" sx={{ width: '100%', height: '24px' }} />
			</PaymentMethodTile>
		);
	}

	if (paymentMethod === 'check') {
		return (
			<PaymentMethodTile>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 100 }}>
					<PaymentMethodIcon paymentMethod="check" />
					<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
						Check on file
					</Typography>
				</Box>
			</PaymentMethodTile>
		);
	}

	if (paymentMethod === 'paypal') {
		return (
			<PaymentMethodTile>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 100 }}>
					<PaymentMethodIcon paymentMethod="paypal" />
					<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
						PayPal connected
					</Typography>
				</Box>
				<Link2Icon sx={({ size }) => ({ width: size.sm, height: size.sm })} />
			</PaymentMethodTile>
		);
	}

	const maskedNumberStripped = maskedNumber?.replaceAll('X', '');

	return (
		<Stack
			sx={{
				flexDirection: { xs: 'column', sm: 'row' },
				alignItems: 'center',
				gap: 100,
			}}
		>
			<PaymentMethodTile>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 100 }}>
					<PaymentMethodIcon
						paymentMethod={paymentMethod}
						issuer={issuer}
						sx={{ width: 24, height: 24, flexShrink: 0 }}
					/>
					<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
						<Box
							component="span"
							sx={{ display: { xs: 'none', sm: 'inline' } }}
						>
							•••• •••• ••••{' '}
						</Box>
						{maskedNumberStripped}
					</Typography>
				</Box>
				<Typography
					variant="body-sm"
					sx={({ vars }) => ({ color: vars.palette.uc.text.weak })}
				>
					{expirationDate}
				</Typography>
			</PaymentMethodTile>
			<HookedTextField
				name="cardCode"
				placeholder="CVV"
				size="large"
				sx={{ width: { xs: '100%', sm: '72px', flexShrink: 0 } }}
			/>
		</Stack>
	);
};

const StartContent = () => {
	const {
		formValues,
		formErrors,
		step,
		clearSensitiveFormValues,
		setFormValues,
		setFormErrors,
		setStep,
	} = useDialogMakePaymentContext();

	const {
		data: paymentProfileDetails,
		isLoading: isLoadingPaymentProfileDetails,
	} = useBillingPaymentProfileDetails({});
	const { payment_method: paymentMethod } = paymentProfileDetails || {};

	const handleSubmit = () => {
		setStep('confirmPayment');
	};

	const invalidCVV = Boolean(
		creditCardValidators.cardCode(formValues.cardCode),
	);

	const continueDisabled =
		Number(formValues.amount) <= 0 ||
		isLoadingPaymentProfileDetails ||
		(paymentMethod === 'credit_card' && invalidCVV);

	const handleChoosePaymentMethod = () => {
		clearSensitiveFormValues();
		setStep('choosePaymentMethod');
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={setFormValues}
			errors={formErrors}
			onErrorsChange={setFormErrors}
			onSubmit={handleSubmit}
		>
			<AmountField />

			<FadeCollapse in={step === 'start'} collapseTimeout={200}>
				<Stack sx={{ gap: 300, marginTop: 400 }}>
					<Stack sx={{ gap: 100 }}>
						<PaymentMethod />

						<Button
							size="medium"
							variant="ghost"
							startIcon={<ArrowLeftRightIcon />}
							onClick={handleChoosePaymentMethod}
						>
							Use a different payment method
						</Button>
					</Stack>

					<HookedSubmit
						size="large"
						color="highlight"
						disabled={continueDisabled}
					>
						Continue
					</HookedSubmit>
				</Stack>
			</FadeCollapse>

			<FadeCollapse in={step === 'choosePaymentMethod'} collapseTimeout={200}>
				<ChoosePaymentMethodContent sx={{ marginTop: 300 }} />
			</FadeCollapse>
		</LWForm>
	);
};
export default StartContent;
